// components/Settings/BankAccountOrder.js

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faCheck, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { Button, Card, Alert } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { getBankAccounts, updateBankAccountDisplayOrder } from '../../services/BankAccountService';
import '../../css/BankAccountOrder.css';

const BankAccountOrder = () => {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountOrder, setBankAccountOrder] = useState([]);
  const [isDragModeActive, setIsDragModeActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');

  // Fetch bank accounts on component mount
  useEffect(() => {
    fetchBankAccounts();
  }, []);

  const fetchBankAccounts = async () => {
    try {
      setIsLoading(true);
      const accounts = await getBankAccounts();
      setBankAccounts(accounts);
    } catch (error) {
      console.error('Error fetching bank accounts:', error.name);
      setError('Failed to load bank accounts');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle starting drag mode for bank accounts
  const handleStartDragMode = () => {
    // Create an ordered array of bank account IDs and display_order values
    const orderedAccounts = bankAccounts.map((account, index) => ({
      id: String(account.id), // Explicitly convert to string
      display_order: account.display_order !== undefined ? account.display_order : index,
      nickname: account.nickname
    }));
    
    // Sort the accounts by display_order
    orderedAccounts.sort((a, b) => a.display_order - b.display_order);
    
    setBankAccountOrder(orderedAccounts);
    setIsDragModeActive(true);
    setShowSuccess(false);
    setError('');
  };
  
  // Function to handle ending drag mode for bank accounts
  const handleEndDragMode = async (save = true) => {
    if (save && bankAccountOrder.length > 0) {
      try {
        setIsLoading(true);
        // Convert string IDs back to numbers for the API
        const orderData = bankAccountOrder.map((account, index) => ({
          id: parseInt(account.id, 10),
          display_order: index
        }));
        
        // Update the display_order on the server
        await updateBankAccountDisplayOrder(orderData);
        
        // Refresh the bank accounts to get the new order
        await fetchBankAccounts();
        
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } catch (error) {
        console.error('Error saving bank account order:', error.name);
        setError('Failed to save bank account order');
      } finally {
        setIsLoading(false);
      }
    }
    
    setIsDragModeActive(false);
  };
  
  // Function to handle drag end
  const handleDragEnd = (result) => {
    if (!result.destination) {
      return; // Dropped outside the list
    }
    
    // Reorder bank account list
    const reorderedAccounts = Array.from(bankAccountOrder);
    const [movedItem] = reorderedAccounts.splice(result.source.index, 1);
    reorderedAccounts.splice(result.destination.index, 0, movedItem);
    
    // Update display_order values for all accounts based on their new positions
    const updatedAccounts = reorderedAccounts.map((account, index) => ({
      ...account,
      display_order: index
    }));
    
    setBankAccountOrder(updatedAccounts);
  };

  if (isLoading && !isDragModeActive) {
    return <div>Loading bank accounts...</div>;
  }

  return (
    <div>
      <h3 className="page-title">Bank Account Order</h3>
      <div style={{ marginLeft: '40px' }}>
        <p>Set the order in which bank accounts appear throughout the application.</p>
      </div>
      
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {showSuccess && (
        <Alert variant="success" dismissible onClose={() => setShowSuccess(false)}>
          Bank account order saved successfully!
        </Alert>
      )}
      
      <div className="d-flex justify-content-start mb-3">
        {!isDragModeActive ? (
          <Button 
            variant="primary" 
            size="sm" 
            onClick={handleStartDragMode}
            disabled={bankAccounts.length === 0}
          >
            <FontAwesomeIcon icon={faSort} className="me-1" />
            Start Reordering
          </Button>
        ) : (
          <div>
            <Button 
              variant="primary" 
              size="sm" 
              onClick={() => handleEndDragMode(true)}
              className="me-2"
              disabled={isLoading}
            >
              <FontAwesomeIcon icon={faCheck} className="me-1" />
              {isLoading ? 'Saving...' : 'Save Order'}
            </Button>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={() => handleEndDragMode(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
      
      {bankAccounts.length === 0 && !isLoading && (
        <Alert variant="info">
          No bank accounts found. Add some bank accounts first to set their order.
        </Alert>
      )}
      
      {/* Drag and drop interface */}
      {isDragModeActive && bankAccountOrder.length > 0 ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="mb-3">
            <Card className="bg-light">
              <Card.Body>
                <h6 className="mb-0">Drag and drop bank accounts to set their display order across the app</h6>
              </Card.Body>
            </Card>
          </div>
          <p className="drag-instructions">
            <FontAwesomeIcon icon={faGripVertical} className="me-2" />
            Drag accounts to reorder how they'll appear throughout the app
          </p>
          <Droppable droppableId="bank-accounts-droppable" direction="vertical">
            {(provided) => (
              <div 
                className="bank-accounts-drag-container"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {bankAccountOrder.map((account, index) => {
                  return (
                    <Draggable 
                      key={`draggable-${account.id}`}
                      draggableId={`draggable-${account.id}`}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`bank-account-card p-3 rounded border mb-2 ${snapshot.isDragging ? 'is-dragging shadow' : 'bg-white'}`}
                          style={{
                            ...provided.draggableProps.style,
                            // Remove any additional styling that might conflict
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <div 
                              {...provided.dragHandleProps}
                              className="drag-handle me-3"
                            >
                              <FontAwesomeIcon icon={faGripVertical} className="text-muted" />
                            </div>
                            <div className="bank-account-text">
                              <span className="fw-bold">{account.nickname}</span>
                              <small className="text-muted ms-2 d-block d-sm-inline">Position: {index + 1}</small>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        /* Show current order when not in drag mode */
        !isDragModeActive && bankAccounts.length > 0 && (
          <div className="bank-accounts-drag-container">
            <h5>Current Order:</h5>
            <div className="mt-3">
              {bankAccounts
                .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                .map((account, index) => (
                  <div key={account.id} className="bank-account-card p-3 rounded border bg-light mb-2">
                    <div className="d-flex align-items-center">
                      <span className="badge bg-secondary me-3">{index + 1}</span>
                      <div className="bank-account-text">
                        <span className="fw-bold">{account.nickname}</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )
      )}

    </div>
  );
};

export default BankAccountOrder;
